import { addMonths } from 'date-fns/addMonths';
import { endOfMonth } from 'date-fns/endOfMonth';
import { endOfQuarter } from 'date-fns/endOfQuarter';
import { getMonth } from 'date-fns/getMonth';
import { getQuarter } from 'date-fns/getQuarter';
import { getYear } from 'date-fns/getYear';
import { isSameDay } from 'date-fns/isSameDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfQuarter } from 'date-fns/startOfQuarter';
import { startOfYear } from 'date-fns/startOfYear';
import { subMonths } from 'date-fns/subMonths';

/**
 * Préréglages de période proposés à la saisie d'un bon de commande.
 *
 * Un BDC couvre presque toujours une période calendaire ronde (le mois qui vient,
 * le trimestre qui vient, le prochain semestre…). Ces préréglages préremplissent
 * les dates de début / fin plutôt que de laisser l'utilisateur les composer à la main.
 */
export type PurchaseOrderDatePresetKind = 'month' | 'quarter' | 'semester';

export type PurchaseOrderDatePreset = {
  /** Identifiant stable et déterministe (ex: `month-2026-08`, `quarter-2026-3`). */
  id: string;
  kind: PurchaseOrderDatePresetKind;
  /** Numéro de trimestre (1-4) ou de semestre (1-2). Absent pour un mois. */
  periodNumber?: number;
  year: number;
  startDate: Date;
  endDate: Date;
};

const SEMESTER_LENGTH_IN_MONTHS = 6;

const toMonthPreset = (date: Date): PurchaseOrderDatePreset => {
  const startDate = startOfMonth(date);
  return {
    id: `month-${getYear(startDate)}-${getMonth(startDate) + 1}`,
    kind: 'month',
    year: getYear(startDate),
    startDate,
    endDate: endOfMonth(startDate),
  };
};

const toQuarterPreset = (date: Date): PurchaseOrderDatePreset => {
  const startDate = startOfQuarter(date);
  return {
    id: `quarter-${getYear(startDate)}-${getQuarter(startDate)}`,
    kind: 'quarter',
    periodNumber: getQuarter(startDate),
    year: getYear(startDate),
    startDate,
    endDate: endOfQuarter(startDate),
  };
};

const startOfSemester = (date: Date): Date => {
  const semesterIndex = getMonth(date) < SEMESTER_LENGTH_IN_MONTHS ? 0 : 1;
  return addMonths(startOfYear(date), semesterIndex * SEMESTER_LENGTH_IN_MONTHS);
};

const toSemesterPreset = (date: Date): PurchaseOrderDatePreset => {
  const startDate = startOfSemester(date);
  const periodNumber = getMonth(startDate) < SEMESTER_LENGTH_IN_MONTHS ? 1 : 2;
  return {
    id: `semester-${getYear(startDate)}-${periodNumber}`,
    kind: 'semester',
    periodNumber,
    year: getYear(startDate),
    startDate,
    endDate: endOfMonth(addMonths(startDate, SEMESTER_LENGTH_IN_MONTHS - 1)),
  };
};

/** Vrai si `date` tombe dans le premier mois de la période commençant à `periodStart`. */
const isInFirstMonthOfPeriod = (date: Date, periodStart: Date): boolean =>
  isSameDay(startOfMonth(date), startOfMonth(periodStart));

/**
 * Construit la courte liste de préréglages pertinents autour de `now`.
 *
 * Règles (volontairement peu nombreuses pour garder 6 chips maximum) :
 * - mois : le mois précédent, le mois en cours et le mois suivant (un BDC arrive
 *   souvent juste avant la période couverte, parfois avec un mois de retard) ;
 * - trimestres : le trimestre précédent + le trimestre en cours quand on est dans
 *   le **premier mois** d'un trimestre (le trimestre écoulé est encore d'actualité),
 *   sinon le trimestre en cours + le suivant ;
 * - semestre : le semestre en cours quand on est dans son premier mois, sinon le
 *   semestre suivant.
 */
export const buildPurchaseOrderDatePresets = (now: Date): PurchaseOrderDatePreset[] => {
  const currentQuarterStart = startOfQuarter(now);
  const quarterPresets = isInFirstMonthOfPeriod(now, currentQuarterStart)
    ? [toQuarterPreset(subMonths(currentQuarterStart, 1)), toQuarterPreset(currentQuarterStart)]
    : [toQuarterPreset(currentQuarterStart), toQuarterPreset(addMonths(currentQuarterStart, 3))];

  const currentSemesterStart = startOfSemester(now);
  const semesterPreset = isInFirstMonthOfPeriod(now, currentSemesterStart)
    ? toSemesterPreset(currentSemesterStart)
    : toSemesterPreset(addMonths(currentSemesterStart, SEMESTER_LENGTH_IN_MONTHS));

  return [
    toMonthPreset(subMonths(now, 1)),
    toMonthPreset(now),
    toMonthPreset(addMonths(now, 1)),
    ...quarterPresets,
    semesterPreset,
  ];
};

/** Vrai si la période saisie correspond exactement au préréglage (jour près). */
export const matchesPurchaseOrderDatePreset = (
  preset: PurchaseOrderDatePreset,
  startDate: Date | null,
  endDate: Date | null,
): boolean =>
  startDate !== null &&
  endDate !== null &&
  isSameDay(startDate, preset.startDate) &&
  isSameDay(endDate, preset.endDate);
